import { Card, Spinner, Text } from '@fluentui/react-components';
import { useAppDispatch, useSelector } from '../../../../store/hooks';
import { useReportingDashboardPanelStyles } from './ReportingDashboardPanel.styles';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo } from 'react';
import {
    loadMetrics,
    setRangeLabels,
    updateFilters,
} from '../../../../store/features/reportingDashboard/reportingDashboardSlice';
import { app } from '@microsoft/teams-js';
import ReportingDashboardToolbar from './ReportingDashboardToolbar/ReportingDashboardToolbar';
import { getDescriptiveRangeLabels } from './reportingDashboardUtils';
import CheckInSection from './sections/CheckInSection';
import GeneralSection from './sections/GeneralSection';
import PerformanceSection from './sections/PerformanceSection';
import ConversationsSection from './sections/ConversationsSection';
import CustomerUpdatesSection from './sections/CustomerUpdatesSection';
import ResponsiveTabList from '../../../../controls/ResponsiveTabList/ResponsiveTabList';
import useResponsiveTabList, {
    ResponsiveTab,
} from '../../../../controls/ResponsiveTabList/useResponsiveTabList';
import ClearFilterButton from '../../../../controls/Buttons/ClearFilterButton';

type TabId =
    | 'general'
    | 'checkIns'
    | 'performance'
    | 'conversations'
    | 'customerUpdates';

const InfoField: React.FC<{
    label: string;
    children: React.ReactNode;
}> = ({ label, children }) => {
    const styles = useReportingDashboardPanelStyles();

    return (
        <div className={styles.field}>
            <Text weight="semibold">{label}</Text>
            {children}
        </div>
    );
};

const InfoRow: React.FC = () => {
    const styles = useReportingDashboardPanelStyles();

    const dispatch = useAppDispatch();
    const { t } = useTranslation();

    const { rangeLabels, filters } = useSelector(
        (state) => state.reportingDashboard
    );

    return (
        <div className={styles.row}>
            <Card className={styles.infoCard}>
                <InfoField label={rangeLabels.currentDescriptive}>
                    {rangeLabels.currentDates}
                </InfoField>
                <InfoField label={rangeLabels.previousDescriptive}>
                    {rangeLabels.previousDates}
                </InfoField>
            </Card>
            {(filters.category || filters.aircraftRegNumber) && (
                <Card className={styles.infoCard}>
                    {filters.category && (
                        <InfoField
                            label={t('ReportingDashboard_Filter_Category')}
                        >
                            <span className={styles.filterInfoValue}>
                                {filters.category}
                                <ClearFilterButton
                                    onClick={() =>
                                        dispatch(
                                            updateFilters({
                                                category: null,
                                            })
                                        )
                                    }
                                />
                            </span>
                        </InfoField>
                    )}
                    {filters.aircraftRegNumber && (
                        <InfoField
                            label={t(
                                'ReportingDashboard_Filter_AircraftRegNumber'
                            )}
                        >
                            <span className={styles.filterInfoValue}>
                                {filters.aircraftRegNumber}
                                <ClearFilterButton
                                    onClick={() =>
                                        dispatch(
                                            updateFilters({
                                                aircraftRegNumber: null,
                                            })
                                        )
                                    }
                                />
                            </span>
                        </InfoField>
                    )}
                </Card>
            )}
        </div>
    );
};

const ReportingDashboardPanel: React.FC = () => {
    const styles = useReportingDashboardPanelStyles();

    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const {
        loadStatus,
        previousRange,
        currentRange,
        selectedDayRange,
        selectedRangeDates,
        filters,
    } = useSelector((state) => state.reportingDashboard);

    useEffect(() => {
        const { current, previous } = getDescriptiveRangeLabels(
            selectedDayRange,
            t
        );

        dispatch(
            setRangeLabels({
                previous,
                current,
            })
        );
    }, [selectedDayRange, dispatch, t]);

    useEffect(() => {
        dispatch(loadMetrics());
    }, [
        selectedDayRange,
        selectedRangeDates.start,
        selectedRangeDates.end,
        filters.aircraftRegNumber,
        filters.category,
        filters.groundTeamId.value,
        dispatch,
    ]);

    useEffect(() => {
        if (loadStatus === 'idle') {
            app.notifySuccess();
        }
    }, [loadStatus]);

    const tabs = useMemo<ResponsiveTab<TabId>[]>(
        () => [
            {
                id: 'general',
                label: t('ReportingDashboard_Section_General'),
                content: <GeneralSection className={styles.section} />,
            },
            {
                id: 'checkIns',
                label: t('ReportingDashboard_Section_CheckIns'),
                content: <CheckInSection className={styles.section} />,
            },
            {
                id: 'performance',
                label: t('ReportingDashboard_Section_Performance'),
                content: <PerformanceSection className={styles.section} />,
            },
            {
                id: 'conversations',
                label: t('ReportingDashboard_Section_Conversations'),
                content: <ConversationsSection className={styles.section} />,
            },
            {
                id: 'customerUpdates',
                label: t('ReportingDashboard_Section_CustomerUpdates'),
                content: <CustomerUpdatesSection className={styles.section} />,
            },
        ],
        [t, styles]
    );

    const { selectedTab, setSelectedTabId } = useResponsiveTabList(
        tabs,
        'general'
    );

    return (
        <div className={styles.root}>
            <ReportingDashboardToolbar />
            {loadStatus === 'idle' && previousRange && currentRange && (
                <Card className={styles.segmentCard}>
                    <InfoRow />
                    <div>
                        <ResponsiveTabList
                            items={tabs}
                            selectedId={selectedTab.id}
                            onTabSelect={setSelectedTabId}
                            tabListProps={{
                                size: 'large',
                                appearance: 'filled-circular',
                            }}
                        />
                    </div>
                    {selectedTab.content}
                </Card>
            )}
            {loadStatus === 'loading' && (
                <Spinner className={styles.spinner} appearance="primary" />
            )}
        </div>
    );
};

export default ReportingDashboardPanel;
